import { useState, useEffect } from 'react';
import {
  X, Shield, Zap, Check, AlertTriangle, RefreshCw, Sparkles,
  Layers, ExternalLink, Gauge, ArrowRight, TrendingUp, MapPin
} from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '../api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const LOADING_STATUSES = [
  "Mapping prospect coordinates...",
  "Querying Google Places for nearby competitors of the same category...",
  "Discovered local competitors in a 5km radius...",
  "Auditing competitor web parameters (SEO, speed, booking widgets)...",
  "Averaging competitor reviews, ratings, and converting capabilities...",
  "AI engine is assessing market gaps and generating sales insights...",
  "Drafting revenue projection metrics and ROI audits..."
];

export default function CompetitorIntelligenceModal({
  isOpen,
  onClose,
  analysis,
  businessName,
  leadId,
  onRefresh,
  prospectRating = 0,
  prospectReviews = 0,
  prospectHasWebsite = false
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [expandedCompetitor, setExpandedCompetitor] = useState(null);

  useEffect(() => {
    let interval;
    if (isRefreshing) {
      interval = setInterval(() => {
        setLoadingTextIndex((prev) => (prev + 1) % LOADING_STATUSES.length);
      }, 2500);
    } else {
      setLoadingTextIndex(0);
    }
    return () => clearInterval(interval);
  }, [isRefreshing]);

  if (!isOpen) return null;

  const handleRefresh = async () => {
    if (!leadId) return;
    setIsRefreshing(true);
    try {
      await api.post(`/leads/${leadId}/analyze-competitors?force=true`);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Refresh failed", err);
      alert("Failed to analyze competitors. Make sure your Lead has exact address/coordinates.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const chartData = analysis ? [
    { name: 'Reviews', Prospect: prospectReviews, AverageCompetitor: Math.round(analysis.averages.reviews_count) },
    { name: 'Rating (x10)', Prospect: Math.round(prospectRating * 10), AverageCompetitor: Math.round(analysis.averages.rating * 10) },
    { name: 'SEO Score', Prospect: 38, AverageCompetitor: Math.round(analysis.averages.seo_score) },
    { name: 'Speed Score', Prospect: 55, AverageCompetitor: Math.round(analysis.averages.performance_score) },
    { name: 'Trust Score', Prospect: 41, AverageCompetitor: Math.round(analysis.averages.trust_score) },
    { name: 'Conversion', Prospect: 35, AverageCompetitor: Math.round(analysis.averages.conversion_score) }
  ] : [];

  const getScoreBg = (score) => {
    if (score >= 70) return "bg-green-500/10 text-green-400 border border-green-500/20";
    if (score >= 40) return "bg-orange-500/10 text-orange-400 border border-orange-500/20";
    return "bg-red-500/10 text-red-400 border border-red-500/20";
  };

  const getPriorityColor = (p) => {
    if (p === "High") return "text-red-400 bg-red-500/10 border border-red-500/20";
    if (p === "Medium") return "text-orange-400 bg-orange-500/10 border border-orange-500/20";
    return "text-green-400 bg-green-500/10 border border-green-500/20";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4 overflow-y-auto">
      {isRefreshing && (
        <div className="absolute inset-0 bg-slate-950/95 z-50 flex flex-col items-center justify-center p-6 text-center">
          <div className="relative w-24 h-24 mb-8">
            <div className="absolute inset-0 border-4 border-indigo-500/20 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            <div className="absolute inset-4 bg-indigo-500/10 rounded-full flex items-center justify-center animate-pulse">
              <Layers className="text-indigo-400" size={28} />
            </div>
          </div>
          <h3 className="text-2xl font-bold tracking-tight text-white mb-2">Analyzing Local Competitors</h3>
          <p className="text-indigo-400/80 text-sm animate-pulse max-w-md h-8 transition-all">{LOADING_STATUSES[loadingTextIndex]}</p>
          <div className="w-64 h-1 bg-white/[0.06] rounded-full overflow-hidden mt-6">
            <div className="h-full bg-indigo-500 animate-[shimmer_2s_infinite]" style={{
              backgroundImage: 'linear-gradient(90deg, transparent, #818cf8, transparent)', backgroundSize: '200% 100%'
            }}></div>
          </div>
        </div>
      )}

      <div className="glass-card w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl border border-white/[0.08] shadow-2xl flex flex-col bg-slate-900/90 text-slate-100">

        <div className="p-6 border-b border-white/[0.06] flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-900/40 backdrop-blur-md">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="p-2 bg-indigo-500/10 text-indigo-400 rounded-xl border border-indigo-500/20"><Layers size={20} /></span>
              <div>
                <h2 className="text-xl font-bold tracking-tight text-white">Competitor Intelligence</h2>
                <p className="text-xs text-slate-400 font-mono">Comparing: {businessName}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 self-stretch md:self-auto justify-end">
            {leadId && (
              <button onClick={handleRefresh} disabled={isRefreshing}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold rounded-xl transition-all border border-indigo-400/20 shadow-lg shadow-indigo-600/15 disabled:opacity-50">
                <RefreshCw size={14} className={cn(isRefreshing && "animate-spin")} /> Refresh Comparison
              </button>
            )}
            <button onClick={onClose} className="p-2 bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.06] rounded-xl transition-colors text-slate-400 hover:text-white">
              <X size={18} />
            </button>
          </div>
        </div>

        {!analysis ? (
          <div className="flex-1 flex flex-col items-center justify-center py-24 text-slate-400">
            <Layers size={48} className="text-slate-600 mb-4 animate-pulse" />
            <p className="text-sm">No competitive intelligence generated for this business yet.</p>
            {leadId && (
              <button onClick={handleRefresh} className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold flex items-center gap-2">
                <RefreshCw size={14} /> Compare Against Competitors
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="px-6 border-b border-white/[0.06] flex gap-4 overflow-x-auto bg-slate-900/10">
              {['overview', 'charts', 'competitors', 'ai'].map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={cn("py-3 text-xs font-semibold border-b-2 transition-all shrink-0 capitalize",
                    activeTab === tab ? "border-indigo-500 text-white font-bold" : "border-transparent text-slate-400 hover:text-slate-200")}>
                  {tab === 'ai' ? 'AI Outreach Analysis' : tab === 'charts' ? 'Compare Charts' : tab}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">

              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-white/[0.06] flex flex-col justify-between">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">AI Comp Score</span>
                      <div className="flex items-baseline gap-1 mt-2">
                        <span className="text-3xl font-extrabold text-white">{analysis.opportunity_score.overall_score}</span>
                        <span className="text-xs text-slate-500 font-semibold">/ 100</span>
                      </div>
                      <span className="text-[10px] text-slate-400 mt-2 truncate block">Competitive Gap Index</span>
                    </div>
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-white/[0.06] flex flex-col justify-between">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Priority</span>
                      <span className={cn("px-2.5 py-1 rounded-lg text-xs font-bold w-fit mt-2 uppercase tracking-wide", getPriorityColor(analysis.opportunity_score.priority))}>
                        {analysis.opportunity_score.priority} Priority
                      </span>
                      <span className="text-[10px] text-slate-400 mt-2 block">Sales outreach urgency</span>
                    </div>
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-white/[0.06] flex flex-col justify-between">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">ROI Estimate</span>
                      <span className="text-xl font-bold text-green-400 block mt-2">{analysis.opportunity_score.estimated_roi}</span>
                      <span className="text-[10px] text-slate-400 mt-2 block">Projected conversion lift</span>
                    </div>
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-white/[0.06] flex flex-col justify-between">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Missed Value</span>
                      <span className="text-xl font-bold text-indigo-400 block mt-2">{analysis.opportunity_score.revenue_potential}</span>
                      <span className="text-[10px] text-slate-400 mt-2 block">Estimated revenue gap</span>
                    </div>
                  </div>

                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                      <TrendingUp size={14} className="text-indigo-400" /> Prospective Gaps Matrix
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.06] space-y-1">
                        <span className="text-[10px] text-slate-500 uppercase tracking-wider block">Reviews</span>
                        <div className="text-lg font-black text-white">{prospectReviews} <span className="text-xs text-slate-500">vs</span> {Math.round(analysis.averages.reviews_count)}</div>
                        <span className={cn("text-[10px] font-semibold block", prospectReviews < analysis.averages.reviews_count ? "text-red-400" : "text-green-400")}>
                          {prospectReviews < analysis.averages.reviews_count ? `-${Math.round(analysis.averages.reviews_count - prospectReviews)} reviews` : `+${Math.round(prospectReviews - analysis.averages.reviews_count)} reviews`}
                        </span>
                      </div>
                      <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.06] space-y-1">
                        <span className="text-[10px] text-slate-500 uppercase tracking-wider block">Google Rating</span>
                        <div className="text-lg font-black text-white">{prospectRating} <span className="text-xs text-slate-500">vs</span> {analysis.averages.rating}</div>
                        <span className={cn("text-[10px] font-semibold block", prospectRating < analysis.averages.rating ? "text-red-400" : "text-green-400")}>
                          {prospectRating < analysis.averages.rating ? `-${(analysis.averages.rating - prospectRating).toFixed(1)} stars` : `+${(prospectRating - analysis.averages.rating).toFixed(1)} stars`}
                        </span>
                      </div>
                      <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.06] space-y-1">
                        <span className="text-[10px] text-slate-500 uppercase tracking-wider block">Booking System</span>
                        <div className="text-xs font-bold text-white mt-1.5">{prospectHasWebsite ? "Missing" : "No Website"}</div>
                        <span className="text-[10px] text-slate-400 block mt-1">{Math.round(analysis.averages.has_booking_pct)}% of competitors use booking</span>
                      </div>
                      <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.06] space-y-1">
                        <span className="text-[10px] text-slate-500 uppercase tracking-wider block">Website Active</span>
                        <div className="text-xs font-bold text-white mt-1.5">{prospectHasWebsite ? "Active Website" : "No Website"}</div>
                        <span className="text-[10px] text-slate-400 block mt-1">{Math.round(analysis.averages.has_website_pct)}% of competitors have sites</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-5 bg-white/[0.02] border border-white/[0.06] rounded-2xl space-y-3">
                    <h4 className="text-sm font-bold text-white flex items-center gap-2">
                      <Sparkles size={16} className="text-indigo-400" /> Strategic Sales Pitch Angles
                    </h4>
                    <div className="grid md:grid-cols-2 gap-3 pt-2">
                      {analysis.sales_insights.map((insight, idx) => (
                        <div key={idx} className="p-3 bg-slate-900/30 border border-white/[0.06] rounded-xl text-xs text-slate-300 leading-relaxed flex items-start gap-2">
                          <Check size={14} className="text-indigo-400 shrink-0 mt-0.5" /> <span>{insight}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'charts' && (
                <div className="space-y-6">
                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06]">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-1.5">
                      <Gauge size={14} className="text-indigo-400" /> Comparative Digital Audits
                    </h4>
                    <div className="w-full h-[280px] bg-slate-950/20 p-2.5 rounded-xl">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                          <XAxis dataKey="name" stroke="#ffffff40" fontSize={11} />
                          <YAxis stroke="#ffffff40" fontSize={11} />
                          <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#ffffff10' }} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="Prospect" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="AverageCompetitor" fill="#818cf8" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-2 text-center">
                      * SEO, Speed, Trust, and Conversion scores are based on the website audits (0-100). Ratings are scaled by 10.
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'competitors' && (
                <div className="space-y-4">
                  <div className="text-xs text-slate-400 font-medium">Found {analysis.competitors.length} local competitor businesses of the same category.</div>
                  <div className="space-y-2">
                    {analysis.competitors.map((comp, idx) => {
                      const isExpanded = expandedCompetitor === comp.name;
                      return (
                        <div key={idx} className="border border-white/[0.06] rounded-2xl bg-white/[0.02] overflow-hidden">
                          <button onClick={() => setExpandedCompetitor(isExpanded ? null : comp.name)}
                            className="w-full p-4 flex items-center justify-between hover:bg-white/[0.03] transition-colors">
                            <div className="text-left">
                              <h4 className="text-sm font-bold text-white">{comp.name}</h4>
                              <p className="text-xs text-slate-400 font-mono mt-1 flex items-center gap-1.5">
                                <MapPin size={12} className="text-indigo-400" /> {comp.distance_km} km away | {comp.rating} ⭐ ({comp.reviews_count} reviews)
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold uppercase", getScoreBg(comp.opportunity_score))}>
                                Opportunity Score: {comp.opportunity_score}%
                              </span>
                              <span className="text-xs text-slate-500 font-bold">{isExpanded ? 'Collapse' : 'Expand'}</span>
                            </div>
                          </button>

                          {isExpanded && (
                            <div className="p-4 border-t border-white/[0.06] bg-slate-950/20 grid md:grid-cols-2 gap-4 text-xs">
                              <div className="space-y-2">
                                <div>
                                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Address</span>
                                  <span className="text-slate-300 font-medium">{comp.address || "Not specified."}</span>
                                </div>
                                {comp.phone && (
                                  <div>
                                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Phone</span>
                                    <span className="text-slate-300 font-mono">{comp.phone}</span>
                                  </div>
                                )}
                                <div>
                                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Website</span>
                                  {comp.website ? (
                                    <a href={comp.website} target="_blank" rel="noreferrer" className="text-indigo-400 font-bold hover:underline flex items-center gap-1">
                                      {comp.website} <ExternalLink size={10} />
                                    </a>
                                  ) : <span className="text-slate-500 italic">No website active.</span>}
                                </div>
                              </div>
                              <div>
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-2">Web Audit Scores</span>
                                {comp.website_intelligence ? (
                                  <div className="grid grid-cols-2 gap-2 text-center">
                                    <div className="bg-white/[0.04] p-2 rounded-xl">
                                      <div className="text-lg font-black text-white">{comp.website_intelligence.seo_audit?.score || 0}%</div>
                                      <span className="text-[10px] text-slate-400">SEO</span>
                                    </div>
                                    <div className="bg-white/[0.04] p-2 rounded-xl">
                                      <div className="text-lg font-black text-white">{comp.website_intelligence.performance_audit?.score || 0}%</div>
                                      <span className="text-[10px] text-slate-400">Speed</span>
                                    </div>
                                    <div className="bg-white/[0.04] p-2 rounded-xl">
                                      <div className="text-lg font-black text-white">{comp.website_intelligence.trust_analysis?.score || 0}%</div>
                                      <span className="text-[10px] text-slate-400">Trust</span>
                                    </div>
                                    <div className="bg-white/[0.04] p-2 rounded-xl">
                                      <div className="text-lg font-black text-white">{comp.website_intelligence.conversion_audit?.score || 0}%</div>
                                      <span className="text-[10px] text-slate-400">Conversion</span>
                                    </div>
                                  </div>
                                ) : <span className="text-slate-500 italic">No web audit data available.</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeTab === 'ai' && (
                <div className="space-y-6">
                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                      <Shield size={14} className="text-indigo-400" /> Comparative Summary Matrix
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <h5 className="text-[11px] font-bold uppercase text-green-400 tracking-wide">Key Strengths</h5>
                        <ul className="space-y-1">
                          {analysis.summary.strengths.map((item, i) => (
                            <li key={i} className="text-xs text-slate-400 flex items-center gap-1.5"><Check size={12} className="text-green-500 shrink-0" /> <span>{item}</span></li>
                          ))}
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <h5 className="text-[11px] font-bold uppercase text-red-400 tracking-wide">Gaps & Weaknesses</h5>
                        <ul className="space-y-1">
                          {analysis.summary.weaknesses.map((item, i) => (
                            <li key={i} className="text-xs text-slate-400 flex items-center gap-1.5"><AlertTriangle size={12} className="text-red-500 shrink-0" /> <span>{item}</span></li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 pt-3 border-t border-white/[0.06]">
                      <div className="space-y-2">
                        <h5 className="text-[11px] font-bold uppercase text-indigo-400 tracking-wide">Competitive Advantages</h5>
                        <ul className="space-y-1">
                          {analysis.summary.competitive_advantages.map((item, i) => (
                            <li key={i} className="text-xs text-slate-400 flex items-center gap-1.5"><ArrowRight size={12} className="text-indigo-400 shrink-0" /> <span>{item}</span></li>
                          ))}
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <h5 className="text-[11px] font-bold uppercase text-orange-400 tracking-wide">Competitive Disadvantages</h5>
                        <ul className="space-y-1">
                          {analysis.summary.competitive_disadvantages.map((item, i) => (
                            <li key={i} className="text-xs text-slate-400 flex items-center gap-1.5"><AlertTriangle size={12} className="text-orange-400 shrink-0" /> <span>{item}</span></li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                      <Zap size={14} className="text-indigo-400" /> AI Actionable Roadmap
                    </h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.06] space-y-2">
                        <span className="text-[10px] font-bold uppercase text-indigo-400 tracking-wide">1. Quick Wins</span>
                        <ul className="space-y-1">{analysis.recommendations.quick_wins.map((item, i) => (<li key={i} className="text-xs text-slate-400 leading-relaxed">• {item}</li>))}</ul>
                      </div>
                      <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.06] space-y-2">
                        <span className="text-[10px] font-bold uppercase text-orange-400 tracking-wide">2. Immediate Actions</span>
                        <ul className="space-y-1">{analysis.recommendations.immediate_actions.map((item, i) => (<li key={i} className="text-xs text-slate-400 leading-relaxed">• {item}</li>))}</ul>
                      </div>
                      <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.06] space-y-2">
                        <span className="text-[10px] font-bold uppercase text-green-400 tracking-wide">3. Long-Term Fixes</span>
                        <ul className="space-y-1">{analysis.recommendations.long_term_improvements.map((item, i) => (<li key={i} className="text-xs text-slate-400 leading-relaxed">• {item}</li>))}</ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                      <Gauge size={14} className="text-indigo-400" /> Outreach Score Breakdown
                    </h4>
                    <p className="text-xs text-slate-400 leading-relaxed">{analysis.opportunity_score.reasoning}</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      {analysis.opportunity_score.score_breakdown.map((item, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b border-white/[0.06] text-xs">
                          <span className="text-slate-300 font-medium">{item.reason}</span>
                          <span className="font-extrabold text-indigo-400">+{item.points} Opportunity Points</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

            </div>

            <div className="p-4 border-t border-white/[0.06] bg-slate-900/20 text-[10px] text-slate-500 flex justify-between items-center px-6">
              <span>Security audited via Google Places API</span>
              <span>Scanned on: {new Date(analysis.comparison_date).toLocaleString()}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
